import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import API from "../../../util/api";
import Report2 from "../../../assets/image/report2.png";
import { showErrorModal } from "../../../util/general";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: ${spin} 1s linear infinite;
`;

const OverlayText = styled.div`
  margin-top: 1em;
  color: white;
  font-size: 1.1em;
  text-align: center;
`;

const Container = styled.div.attrs({ id: "bigbox" })`
  background: #f8f8f8;
  padding: 1.5em;
`;

const Heading = styled.h1`
  margin: 0 0 0.5em;
  color: ${({ colors }) => colors.labelText};
`;

const SectionsLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

const Section = styled.div`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  text-align: center;
  flex: 1 1 ${({ width }) => `${width}px`};
  min-width: 180px;
  max-width: ${({ width }) => `${width}px`};
  margin-top: 10px;
`;

const Card3D = styled.div`
  perspective: 800px;
  margin-top: 20px;
  margin-left: 30px;
  width: 160px;

  & > div {
    background: white;
    border-radius: 5px;
    transform: rotateX(6deg) rotateY(-4deg);
    transform-origin: bottom center;
    box-shadow: 6px 12px 20px rgba(0, 0, 0, 0.15);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: rotateX(0deg) rotateY(0deg) translateY(-5px);
      box-shadow: 8px 16px 24px rgba(0, 0, 0, 0.2);
    }
  }
`;

const CardInner = styled.div`
  padding: 10px;
`;

const ReportImage = styled.img`
  display: block;
  width: 150px;
  height: 194px;
  margin: 0 auto;
  border-radius: 3px;
  cursor: pointer;
`;

const PDFLink = styled.a`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 13px;
  color: #2c4960;
  text-decoration: none;
  margin: 0 8px;
  display: inline-block;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Links = styled.div`
  margin-top: 8px;
`;

export default function EducatorModuleDownloadReport({ colors }) {
  const [loading, setLoading] = useState(false);
  const { invoice_id: invoiceId } =
    useSelector((s) => s.auth.selected_subscriptions) || {};

  const downloadReport = async () => {
    if (!invoiceId) {
      showErrorModal("No subscription selected.");
      return;
    }

    setLoading(true);
    try {
      const { blob } = await API.downloadEducatorModuleDownloadReport({
        invoice_id: invoiceId,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EducatorModuleDownloadReport_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      showErrorModal("Unable to download report: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <Spinner colors={colors} />
          <OverlayText>Requesting report… please wait</OverlayText>
        </LoadingOverlay>
      )}

      <Container colors={colors}>
        <Heading colors={colors}>Educator Module Download Report</Heading>

        <SectionsLayout>
          <Section width={220}>
            Educator Module Download Report
            <Card3D>
              <CardInner>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadReport();
                  }}
                >
                  <ReportImage
                    src={Report2}
                    alt="Educator Module Download Report"
                  />
                </a>
              </CardInner>
            </Card3D>
            <Links>
              <PDFLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadReport();
                }}
              >
                pdf
              </PDFLink>
            </Links>
          </Section>
        </SectionsLayout>
      </Container>
    </>
  );
}
